import React, { useState } from 'react';
import {
  Package, Search, Filter, Download, Eye, MapPin, Calendar,
  Weight, Truck, CheckCircle, Clock, AlertCircle, ChevronDown,
  X, ArrowRight
} from 'lucide-react';

// Mock shipments data
const mockShipments = [
  {
    id: 'FS-2025-4521',
    tracking: 'BN-45821-JM',
    customer: { name: 'Marcus Johnson', code: 'MJ-4521' },
    status: 'in_transit',
    origin: 'Miami, FL',
    destination: 'Kingston, JA',
    weight: '12.5 lbs',
    date: 'Dec 15, 2025',
    eta: 'Dec 18, 2025',
    value: 'JMD 6,546'
  },
  {
    id: 'FS-2025-4520',
    tracking: 'BN-45820-JM',
    customer: { name: 'Sarah Williams', code: 'SW-3892' },
    status: 'delivered',
    origin: 'Miami, FL',
    destination: 'Montego Bay, JA',
    weight: '8.2 lbs',
    date: 'Dec 14, 2025',
    deliveredDate: 'Dec 16, 2025',
    value: 'JMD 4,230'
  },
  {
    id: 'FS-2025-4519',
    tracking: 'BN-45819-JM',
    customer: { name: 'James Brown', code: 'JB-2741' },
    status: 'ready',
    origin: 'Orlando, FL',
    destination: 'Portmore, JA',
    weight: '22.1 lbs',
    date: 'Dec 14, 2025',
    value: 'JMD 11,450'
  },
  {
    id: 'FS-2025-4518',
    tracking: 'BN-45818-JM',
    customer: { name: 'Emily Davis', code: 'ED-1523' },
    status: 'awaiting_payment',
    origin: 'Miami, FL',
    destination: 'Kingston, JA',
    weight: '5.8 lbs',
    date: 'Dec 13, 2025',
    value: 'JMD 3,120'
  },
  {
    id: 'FS-2025-4517',
    tracking: 'BN-45817-JM',
    customer: { name: 'Michael Chen', code: 'MC-4102' },
    status: 'delivered',
    origin: 'Miami, FL',
    destination: 'Spanish Town, JA',
    weight: '15.3 lbs',
    date: 'Dec 13, 2025',
    deliveredDate: 'Dec 15, 2025',
    value: 'JMD 7,890'
  },
  {
    id: 'FS-2025-4516',
    tracking: 'BN-45816-JM',
    customer: { name: 'Lisa Anderson', code: 'LA-3891' },
    status: 'in_transit',
    origin: 'Miami, FL',
    destination: 'Kingston, JA',
    weight: '9.7 lbs',
    date: 'Dec 12, 2025',
    eta: 'Dec 17, 2025',
    value: 'JMD 5,120'
  },
];

const statusFilters = [
  { value: 'all', label: 'All Shipments' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'ready', label: 'Ready for Pickup' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'awaiting_payment', label: 'Awaiting Payment' },
];

export default function TenantShipments() {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const filteredShipments = mockShipments.filter(s => {
    const matchesFilter = filter === 'all' || s.status === filter;
    const matchesSearch = s.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.tracking.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircle size={14} />;
      case 'in_transit': return <Truck size={14} />;
      case 'ready': return <Package size={14} />;
      case 'awaiting_payment': return <Clock size={14} />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'lime';
      case 'in_transit': return 'blue';
      case 'ready': return 'cyan';
      case 'awaiting_payment': return 'amber';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'delivered': return 'Delivered';
      case 'in_transit': return 'In Transit';
      case 'ready': return 'Ready';
      case 'awaiting_payment': return 'Awaiting Payment';
      default: return status;
    }
  };

  const openDetail = (shipment) => {
    setSelectedShipment(shipment);
    setShowDetailModal(true);
  };

  // Summary stats
  const stats = {
    total: mockShipments.length,
    inTransit: mockShipments.filter(s => s.status === 'in_transit').length,
    delivered: mockShipments.filter(s => s.status === 'delivered').length,
    ready: mockShipments.filter(s => s.status === 'ready').length,
  };

  return (
    <div className="tenant-shipments-page" data-testid="tenant-shipments">
      {/* Summary Stats */}
      <div className="shipments-summary-row">
        <div className="summary-stat">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="summary-stat blue">
          <span className="stat-value">{stats.inTransit}</span>
          <span className="stat-label">In Transit</span>
        </div>
        <div className="summary-stat lime">
          <span className="stat-value">{stats.delivered}</span>
          <span className="stat-label">Delivered</span>
        </div>
        <div className="summary-stat cyan">
          <span className="stat-value">{stats.ready}</span>
          <span className="stat-label">Ready</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="shipments-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search shipments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="shipment-search"
          />
        </div>
        
        <div className="toolbar-actions">
          <div className="filter-dropdown">
            <Filter size={16} />
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              data-testid="status-filter"
            >
              {statusFilters.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          
          <button className="btn btn-secondary" data-testid="export-btn">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* Shipments Table */}
      <div className="shipments-table-card">
        <div className="table-header-row">
          <span>Shipment ID</span>
          <span>Customer</span>
          <span>Status</span>
          <span>Route</span>
          <span>Weight</span>
          <span>Value</span>
          <span>Actions</span>
        </div>
        
        {filteredShipments.map((shipment) => (
          <div key={shipment.id} className="table-data-row" data-testid={`row-${shipment.id}`}>
            <div className="cell-shipment-id">
              <span className="shipment-id">{shipment.id}</span>
              <span className="tracking-id">{shipment.tracking}</span>
            </div>
            
            <div className="cell-customer">
              <span className="customer-name">{shipment.customer.name}</span>
              <span className="customer-code">{shipment.customer.code}</span>
            </div>
            
            <div className="cell-status">
              <span className={`status-badge ${getStatusColor(shipment.status)}`}>
                {getStatusIcon(shipment.status)}
                {getStatusLabel(shipment.status)}
              </span>
            </div>
            
            <div className="cell-route">
              <span className="route-text">{shipment.origin}</span>
              <ArrowRight size={12} />
              <span className="route-text">{shipment.destination}</span>
            </div>
            
            <div className="cell-weight">{shipment.weight}</div>
            
            <div className="cell-value">{shipment.value}</div>
            
            <div className="cell-actions">
              <button 
                className="action-btn"
                onClick={() => openDetail(shipment)}
                data-testid={`view-${shipment.id}`}
              >
                <Eye size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedShipment && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div 
            className="modal-content shipment-detail-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="shipment-detail-modal"
          >
            <div className="modal-header">
              <div>
                <h3>Shipment Details</h3>
                <span className="modal-subtitle">{selectedShipment.id}</span>
              </div>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className={`status-hero ${getStatusColor(selectedShipment.status)}`}>
                {getStatusIcon(selectedShipment.status)}
                <span>{getStatusLabel(selectedShipment.status)}</span>
              </div>

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Tracking Number</span>
                  <span className="detail-value mono">{selectedShipment.tracking}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Customer</span>
                  <span className="detail-value">{selectedShipment.customer.name}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Customer Code</span>
                  <span className="detail-value mono">{selectedShipment.customer.code}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Weight</span>
                  <span className="detail-value">{selectedShipment.weight}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Origin</span>
                  <span className="detail-value">{selectedShipment.origin}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Destination</span>
                  <span className="detail-value">{selectedShipment.destination}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Ship Date</span>
                  <span className="detail-value">{selectedShipment.date}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{selectedShipment.deliveredDate ? 'Delivered' : 'ETA'}</span>
                  <span className="detail-value">{selectedShipment.deliveredDate || selectedShipment.eta}</span>
                </div>
                <div className="detail-item full-width">
                  <span className="detail-label">Value</span>
                  <span className="detail-value large">{selectedShipment.value}</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </button>
              <button className="btn btn-primary" data-testid="track-shipment-btn">
                Track Shipment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
